import { Box, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useTheme } from 'next-themes';
import React from 'react';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';
import { Toaster } from 'sonner';

// Legacy AdminTemplatesPage removed — replaced by TemplatesV2Page at /templates-v2
import { BulkFinchGPTActions } from './components/bulk-finchgpt-actions/BulkFinchGPTActions';
import { CaseIngestionPage } from './components/case-ingestion';
import { CaseIngestionPageV2 } from './components/case-ingestion-v2';
import { ClaimFilingPage } from './components/claims-filing';
import {
  ResearchPropertyPage,
  ReviewIncidentPropertiesPage,
} from './components/claims-filing/premises';
import { BlandCommandCenter } from './components/command-center/BlandCommandCenter';
import { BPOCommandCenter } from './components/command-center/BPOCommandCenter';
import { ContactQueuePage } from './components/contact-queue';
import { NotificationProvider } from './components/contexts/NotificationContext';
import { DraftLorPage } from './components/coverage/DraftLorPage';
import { ReviewSendLorPage } from './components/coverage/ReviewSendLorPage';
import { TemplateDetailPage } from './components/demand-example/templates/TemplateDetailPage';
import { TemplateEditPage } from './components/demand-example/templates/TemplateEditPage';
import { TemplatesPage } from './components/demand-example/templates/TemplatesPage';
import { SyncSuggestionCards } from './components/entity-sync/SyncSuggestionCards';
import { SyncSuggestionCardsCompact } from './components/entity-sync/SyncSuggestionCardsCompact';
import { FirmsLayout } from './components/firms/FirmsLayout';
import { CheckinSettingsPage } from './components/friday-checkin/CheckinSettingsPage';
import { FrontPlugin } from './components/front-plugin/front-plugin';
import { CreateIntakePage, IntakeFormPage } from './components/intakes/pages';
import { LeadsPage } from './components/leads/LeadsPage';
import { RiskOverviewPage } from './components/mobrain/RiskOverviewPage';
import AuthCallback from './components/pages/AuthCallback';
import { Call } from './components/pages/Call';
import { CreateMatterPage } from './components/pages/CreateMatterPage';
import { Day1Page } from './components/pages/Day1Page';
import { ExternalTasksPage } from './components/pages/ExternalTasksPage';
import { ForgotPasswordForm } from './components/pages/ForgotPasswordForm';
import { IntakesPage } from './components/pages/IntakesPage';
import { InternalPage } from './components/pages/InternalPage';
import LoginForm from './components/pages/LoginForm';
import MatterLayout from './components/pages/MatterLayout';
import { MattersPage } from './components/pages/MattersPage';
import { MedicalRecordsDashboardPage } from './components/pages/MedicalRecordsDashboardPage';
import { PoliceReportsDashboardPage } from './components/pages/PoliceReportsDashboardPage';
import { ResetPasswordForm } from './components/pages/ResetPasswordForm';
import SignUpForm from './components/pages/SignUpForm';
import { UpdateMatterPage } from './components/pages/UpdateMatterPage';
import { VoiceAiDashboardPage } from './components/pages/VoiceAiDashboardPage';
import { TaskDistributionPage } from './pages/TaskDistribution/TaskDistributionPage';
import { ProfileSettingsPage } from './components/profile/ProfileSettingsPage';
import FrontPluginRoute from './components/routing/FrontPluginRoute';
import { NavigationBlockerProvider } from './components/routing/NavigationBlockerProvider';
import ProtectedRoute from './components/routing/ProtectedRoute';
import { PublicRoute } from './components/routing/PublicRoute';
import SessionManager from './components/routing/SessionManager';
import { NotFound } from './components/shared-matter/NotFound';
import { SharedMatter } from './components/shared-matter/SharedMatter';
import { TasksVerticalRouter } from './components/tasks-datatable/TasksVerticalRouter';
import { TelephonyMiniDialerHost } from './components/telephony/TelephonyMiniDialerHost';
import { OperatorTemplateManagerPage } from './components/template-editor/OperatorTemplateManagerPage';
import { TemplateManagerPage } from './components/template-editor/TemplateManagerPage';
import { TemplateBlocksPlayground } from './components/templates-v2/TemplateBlocksPlayground';
import { TemplateDetailPage as TemplateV2DetailPage } from './components/templates-v2/TemplateDetailPage';
import { TemplateGeneratePage } from './components/templates-v2/TemplateGeneratePage';
import { TemplatesV2Page } from './components/templates-v2/TemplatesV2Page';
import { UserDetailPage } from './components/users/UserDetailPage';
import { BulkCreateProvider } from './contexts/BulkCreateContext';
import { DocumentTitleProvider } from './contexts/DocumentTitleContext';
import { FavoritedMattersProvider } from './contexts/FavoritedMattersContext';
import { PushChangesProvider } from './contexts/PushChangesContext';
import { PushNoteToCmsProvider } from './contexts/PushNoteToCmsContext';
import { TelephonyCtiProvider } from './contexts/TelephonyCtiContext';
import { MainLayout } from './layout/MainLayout';
import { AgentflowEvalPage } from './pages/agentflow/AgentflowEvalPage';
import { AgentflowConfigPage } from './pages/AgentflowConfigPage';
import { BPOMedicalRecordsOutcomePage } from './pages/bpo/BPOMedicalRecordsOutcomePage';
import { BPOOutcomePage } from './pages/bpo/BPOOutcomePage';
import { BPOStartPage } from './pages/bpo/BPOStartPage';
import { BPOTranscriptReviewPage } from './pages/bpo/BPOTranscriptReviewPage';
import {
  BulkAgentOperationDetailPage,
  BulkAgentOperationsPage,
} from './pages/BulkAgentOperationsPage';
import { BulkBillingCodePage } from './pages/BulkBillingCodePage';
import { MiniTortClaimDetail } from './components/mini-tort/MiniTortClaimDetail';
import { MiniTortDashboard } from './components/mini-tort/MiniTortDashboard';
import {
  BulkCreateOperationDetailPage,
  BulkCreateOperationsPage,
} from './pages/BulkCreateOperationsPage';
import CarrierDirectory from './pages/CarrierDirectory';
import { ClaimsPanopticonPage } from './pages/ClaimsPanopticonPage';
import { CMSImportDashboard } from './pages/CMSImport/CMSImportDashboard';
import { CMSImportDetailPage } from './pages/CMSImport/CMSImportDetailPage';
import { CrossMatterChangeDetailPage } from './pages/CrossMatterChanges/CrossMatterChangeDetailPage';
import { CrossMatterChangesListPage } from './pages/CrossMatterChanges/CrossMatterChangesListPage';
import { TaskImportRunDetail } from './pages/ExternalCMS/TaskImportRunDetail';
import { TaskImportRunMatterTasks } from './pages/ExternalCMS/TaskImportRunMatterTasks';
import { TaskImportRunsDashboard } from './pages/ExternalCMS/TaskImportRunsDashboard';
import { FeatureFeedbackPage } from './pages/FeatureFeedback';
import { IntakyAllFirmsPage } from './pages/IntakyAllFirmsPage';
import { IntakyFirmPage } from './pages/IntakyFirmPage';
import { IntakyTvPage } from './pages/IntakyTvPage';
import { LOAScanPage } from './pages/LOAScanPage';
import { MatterUpdatesDetailPage } from './pages/MatterUpdates/MatterUpdatesDetailPage';
import { MatterUpdatesPage } from './pages/MatterUpdates/MatterUpdatesPage';
import { NotificationsPage } from './pages/Notifications';
import { PoliceDepartmentCreatePage } from './pages/PoliceDepartments/PoliceDepartmentCreatePage';
import { PoliceDepartmentDetailPage } from './pages/PoliceDepartments/PoliceDepartmentDetailPage';
import { PoliceDepartmentsListPage } from './pages/PoliceDepartments/PoliceDepartmentsListPage';
import PoliceReports from './pages/PoliceReports';
import ChequesPage from './pages/postgrid/ChequesPage';
import { ContactDetailPage } from './pages/postgrid/ContactDetailPage';
import ContactManagementPage from './pages/postgrid/ContactManagementPage';
import { CorrespondencePage } from './pages/postgrid/correspondence';
import { CreateContactPage } from './pages/postgrid/CreateContactPage';
import LettersPage from './pages/postgrid/LettersPage';
import { PostgridDashboard } from './pages/postgrid/PostgridDashboard';
import PresidentialBriefPage from './pages/presidential-brief/PresidentialBriefPage';
import ProviderDirectory from './pages/ProviderDirectory';
import { QuantModePage } from './pages/QuantModePage';
import { ResourceEmbedPage } from './pages/ResourceEmbedPage';
import { ResourcesHub } from './pages/ResourcesHub';
import { SettingsHub } from './pages/SettingsHub';
import { UpworkStartPage } from './pages/upwork/UpworkStartPage';
import { WrappedPage } from './pages/WrappedPage';
import { ZaddyPage } from './pages/Zaddy';
import { ZeyomPage } from './pages/Zeyom';
import { ThemeProvider } from './providers/ThemeProvider';
import { store } from './state/state';
import { getTheme } from './theme/theme';

// MUI theme sync component that responds to next-themes
const MuiThemeSync: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { resolvedTheme } = useTheme();
  const muiTheme = getTheme((resolvedTheme as 'light' | 'dark') || 'light');
  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
};

// Generic wrapper that forces components to remount when route params change
// This ensures all child components get fresh state when navigating between entities
const withRouteKey = <P extends object>(
  Component: React.ComponentType<P>,
  paramName: string,
) => {
  const Wrapper: React.FC<P> = (props) => {
    const params = useParams<Record<string, string>>();
    return <Component key={params[paramName]} {...props} />;
  };
  Wrapper.displayName = `WithRouteKey(${Component.displayName || Component.name || 'Component'})`;
  return Wrapper;
};

// Wrappers for routes that can be navigated to via quick search
const MatterLayoutWrapper = withRouteKey(MatterLayout, 'matter_id');
const IntakeFormPageWrapper = withRouteKey(IntakeFormPage, 'intake_id');
const FirmDetailsPageWrapper = withRouteKey(FirmsLayout, 'firm_id');
const PoliceDepartmentDetailPageWrapper = withRouteKey(PoliceDepartmentDetailPage, 'departmentId');
const UserDetailPageWrapper = withRouteKey(UserDetailPage, 'userId');
const ContactDetailPageWrapper = withRouteKey(ContactDetailPage, 'contactId');
const MatterUpdatesDetailPageWrapper = withRouteKey(MatterUpdatesDetailPage, 'matterId');

const App: React.FC = () => {
  return (
    <ThemeProvider defaultTheme="light">
      <Box className="App" sx={{ display: 'flex', backgroundColor: '#fff' }}>
        <Provider store={store}>
          <MuiThemeSync>
            <NotificationProvider>
              <SessionManager>
                <Router>
                  <DocumentTitleProvider>
                    <TelephonyCtiProvider>
                      <BulkCreateProvider>
                        <PushChangesProvider>
                          <PushNoteToCmsProvider>
                          <FavoritedMattersProvider>
                            <TelephonyMiniDialerHost />
                            <Toaster position="bottom-right" />
                            <NavigationBlockerProvider>
                              <Routes>
                                {/* Public BPO routes - no auth, no layout */}
                                <Route
                                  path="/bpo/start"
                                  element={<BPOStartPage />}
                                />
                              <Route
                                path="/bpo/outcome"
                                element={<BPOOutcomePage />}
                              />
                              <Route
                                path="/bpo/medical-records-outcome"
                                element={<BPOMedicalRecordsOutcomePage />}
                              />
                              <Route
                                path="/bpo/transcript-review"
                                element={<BPOTranscriptReviewPage />}
                              />
                              {/* Upwork call-start page - public, no auth */}
                              <Route
                                path="/upwork/start"
                                element={<UpworkStartPage />}
                              />
                              {/* AgentFlow evaluation - public, no auth for BPO agents */}
                              <Route
                                path="/eval/:matter_id/:approval_id"
                                element={<AgentflowEvalPage />}
                              />
                              <Route element={<FrontPluginRoute />}>
                                <Route
                                  path="/front-plugin"
                                  element={<FrontPlugin />}
                                />
                              </Route>
                              <Route
                                path="/shared-matter/:token"
                                element={<SharedMatter />}
                              />

                              {/* Intaky — public firm-facing lead activity dashboard */}
                              <Route
                                path="/external/intake/all"
                                element={<IntakyAllFirmsPage />}
                              />
                              <Route
                                path="/external/intake/tv"
                                element={<IntakyTvPage />}
                              />
                              <Route
                                path="/external/intake/:firmId/tv"
                                element={<IntakyTvPage />}
                              />
                              <Route
                                path="/external/intake/:firmId"
                                element={<IntakyFirmPage />}
                              />

                              {/* PostGrid routes */}
                              <Route element={<ProtectedRoute />}>
                                <Route element={<MainLayout />}>
                                  <Route
                                    path="/postgrid"
                                    element={<PostgridDashboard />}
                                  />
                                  <Route
                                    path="/postgrid/dashboard"
                                    element={<PostgridDashboard />}
                                  />
                                  <Route
                                    path="/postgrid/correspondence"
                                    element={<CorrespondencePage />}
                                  />
                                  <Route
                                    path="/postgrid/contacts"
                                    element={<ContactManagementPage />}
                                  />
                                  <Route
                                    path="/postgrid/contacts/:contactId"
                                    element={<ContactDetailPageWrapper />}
                                  />
                                  <Route
                                    path="/postgrid/create-contact"
                                    element={<CreateContactPage />}
                                  />
                                  <Route
                                    path="/postgrid/cheques"
                                    element={<ChequesPage />}
                                  />
                                  <Route
                                    path="/postgrid/letters"
                                    element={<LettersPage />}
                                  />
                                </Route>
                              </Route>

                              <Route element={<PublicRoute />}>
                                <Route
                                  path="/signup"
                                  element={<SignUpForm />}
                                />
                                <Route path="/login" element={<LoginForm />} />
                                <Route
                                  path="/auth/callback"
                                  element={<AuthCallback />}
                                />
                                <Route
                                  path="/forgot-password"
                                  element={<ForgotPasswordForm />}
                                />
                                <Route
                                  path="/reset-password"
                                  element={<ResetPasswordForm />}
                                />
                                <Route
                                  path="/share/not_found"
                                  element={<NotFound />}
                                />
                              </Route>

                              {/* Wrapped page - full screen, no layout */}
                              <Route element={<ProtectedRoute />}>
                                <Route
                                  path="/wrapped"
                                  element={<WrappedPage />}
                                />
                              </Route>

                              {/* Presidential Brief - full screen, no layout */}
                              <Route element={<ProtectedRoute />}>
                                <Route
                                  path="/presidential-brief"
                                  element={<PresidentialBriefPage />}
                                />
                              </Route>

                              {/* Case ingestion - full screen immersive experience, no layout */}
                              <Route element={<ProtectedRoute />}>
                                {/* V2 is the default */}
                                <Route
                                  path="/matters/ingestion"
                                  element={<CaseIngestionPageV2 />}
                                />
                                <Route
                                  path="/matters/:matterId/ingestion"
                                  element={<CaseIngestionPageV2 />}
                                />
                                {/* V2 explicit path (kept for backwards compat) */}
                                <Route
                                  path="/matters/ingestion/v2"
                                  element={<CaseIngestionPageV2 />}
                                />
                                <Route
                                  path="/matters/:matterId/ingestion/v2"
                                  element={<CaseIngestionPageV2 />}
                                />
                                {/* V1 legacy */}
                                <Route
                                  path="/matters/ingestion/v1"
                                  element={<CaseIngestionPage />}
                                />
                                <Route
                                  path="/matters/:matterId/ingestion/v1"
                                  element={<CaseIngestionPage />}
                                />
                              </Route>

                              {/* Protected routes with shared layout */}
                              <Route element={<ProtectedRoute />}>
                                <Route element={<MainLayout />}>
                                  {/* Day 1 route */}
                                  <Route path="/day-1" element={<Day1Page />} />

                                  {/* Matters routes */}
                                  <Route
                                    path="/matters"
                                    element={<MattersPage />}
                                  />
                                  <Route
                                    path="/matters/create"
                                    element={<CreateMatterPage />}
                                  />
                                  <Route
                                    path="/matters/:matter_id/edit"
                                    element={<UpdateMatterPage />}
                                  />

                                  {/* Claims Filing */}
                                  <Route
                                    path="/matters/:matter_id/coverage/:coverage_id/claims-review"
                                    element={<ClaimFilingPage />}
                                  />
                                  <Route
                                    path="/matters/:matter_id/coverage/:coverage_id/review-send-lor"
                                    element={<ReviewSendLorPage />}
                                  />
                                  <Route
                                    path="/matters/:matter_id/coverage/:coverage_id/draft-lor"
                                    element={<DraftLorPage />}
                                  />

                                  {/* Premises Liability */}
                                  <Route
                                    path="/matters/:matter_id/premises/:coverage_id/confirm-location"
                                    element={<ReviewIncidentPropertiesPage />}
                                  />
                                  <Route
                                    path="/matters/:matter_id/premises/:coverage_id/research-property"
                                    element={<ResearchPropertyPage />}
                                  />

                                  {/* Routes using MatterLayout */}
                                  <Route
                                    path="/matters/:matter_id/*"
                                    element={<MatterLayoutWrapper />}
                                  />

                                  {/* Tasks routes */}
                                  <Route
                                    path="/tasks"
                                    element={<TasksVerticalRouter />}
                                  />
                                  <Route
                                    path="/tasks/:task_id?"
                                    element={<TasksVerticalRouter />}
                                  />

                                  {/* Contact Queue - Treatment SMS outreach */}
                                  <Route
                                    path="/contact-queue"
                                    element={<ContactQueuePage />}
                                  />

                                  {/* External Tasks routes */}
                                  <Route
                                    path="/external-tasks"
                                    element={<ExternalTasksPage />}
                                  />
                                  <Route
                                    path="/external-tasks/:external_task_id"
                                    element={<ExternalTasksPage />}
                                  />

                                  {/* Bulk Create Operations */}
                                  <Route
                                    path="/external-tasks/bulk-create-operations"
                                    element={<BulkCreateOperationsPage />}
                                  />
                                  <Route
                                    path="/external-tasks/bulk-create-operations/:operationId"
                                    element={<BulkCreateOperationDetailPage />}
                                  />

                                  {/* Mini-Tort Claims */}
                                  <Route
                                    path="/mini-tort/claims"
                                    element={<MiniTortDashboard />}
                                  />
                                  <Route
                                    path="/mini-tort/claims/:claimId"
                                    element={<MiniTortClaimDetail />}
                                  />

                                  {/* Demand Example Templates */}
                                  <Route
                                    path="/demand-examples/:demand_example_id/templates"
                                    element={<TemplatesPage />}
                                  />
                                  <Route
                                    path="/demand-examples/:demand_example_id/templates/:template_id"
                                    element={<TemplateDetailPage />}
                                  />
                                  <Route
                                    path="/demand-examples/:demand_example_id/templates/:template_id/edit"
                                    element={<TemplateEditPage />}
                                  />

                                  {/* Police Reports route */}
                                  <Route
                                    path="/police-reports"
                                    element={<PoliceReports />}
                                  />

                                  {/* Medical Records Dashboard */}
                                  <Route
                                    path="/medical-records"
                                    element={<MedicalRecordsDashboardPage />}
                                  />

                                  {/* Police Reports Dashboard */}
                                  <Route
                                    path="/police-reports-dashboard"
                                    element={<PoliceReportsDashboardPage />}
                                  />

                                  {/* Carrier Directory POC */}
                                  <Route
                                    path="/carrier-directory"
                                    element={<CarrierDirectory />}
                                  />

                                  {/* Police Departments */}
                                  <Route
                                    path="/policedepartments"
                                    element={<PoliceDepartmentsListPage />}
                                  />
                                  <Route
                                    path="/policedepartments/create"
                                    element={<PoliceDepartmentCreatePage />}
                                  />
                                  <Route
                                    path="/policedepartments/:departmentId"
                                    element={<PoliceDepartmentDetailPageWrapper />}
                                  />

                                  {/* Cross-Matter Changes */}
                                  <Route
                                    path="/cross-matter-changes"
                                    element={<CrossMatterChangesListPage />}
                                  />
                                  <Route
                                    path="/cross-matter-changes/:matterId"
                                    element={<CrossMatterChangeDetailPage />}
                                  />

                                  {/* Matter Updates (Entity Changes) */}
                                  <Route
                                    path="/matter-updates"
                                    element={<MatterUpdatesPage />}
                                  />
                                  <Route
                                    path="/matter-updates/:matterId"
                                    element={<MatterUpdatesDetailPageWrapper />}
                                  />

                                  {/* Provider Directory (v2 browse) */}
                                  <Route
                                    path="/provider-directory/*"
                                    element={<ProviderDirectory />}
                                  />


                                  {/* Resources Hub */}
                                  <Route
                                    path="/resources"
                                    element={<ResourcesHub />}
                                  />

                                  {/* Settings Hub */}
                                  <Route
                                    path="/settings"
                                    element={<SettingsHub />}
                                  />
                                  <Route
                                    path="/resources/:slug"
                                    element={<ResourceEmbedPage />}
                                  />

                                  {/* Firms routes */}
                                  <Route
                                    path="/firms/*"
                                    element={<FirmDetailsPageWrapper />}
                                  />
                                  {/* Admin Templates — removed, use /templates-v2 */}
                                  {/* Users routes */}
                                  <Route
                                    path="/users/:userId"
                                    element={<UserDetailPageWrapper />}
                                  />

                                  {/* Notifications */}
                                  <Route
                                    path="/notifications"
                                    element={<NotificationsPage />}
                                  />

                                  {/* Voice AI Dashboard */}
                                  <Route
                                    path="/voice-ai-dashboard"
                                    element={<VoiceAiDashboardPage />}
                                  />

                                  {/* Task Distribution */}
                                  <Route
                                    path="/task-distribution"
                                    element={<TaskDistributionPage />}
                                  />

                                  {/* Feature Feedback */}
                                  <Route
                                    path="/my-finch"
                                    element={<FeatureFeedbackPage />}
                                  />

                                  {/* Profile Settings */}
                                  <Route
                                    path="/profile"
                                    element={<ProfileSettingsPage />}
                                  />

                                  {/* External CMS Reconciliation (under Profile) */}
                                  <Route
                                    path="/profile/external-cms"
                                    element={<TaskImportRunsDashboard />}
                                  />
                                  <Route
                                    path="/profile/external-cms/runs/:runId"
                                    element={<TaskImportRunDetail />}
                                  />
                                  <Route
                                    path="/profile/external-cms/runs/:runId/matters/:matterLinkId"
                                    element={<TaskImportRunMatterTasks />}
                                  />

                                  {/* CMS Import (under Profile) */}
                                  <Route
                                    path="/profile/cms-import"
                                    element={<CMSImportDashboard />}
                                  />
                                  <Route
                                    path="/profile/cms-import/:importId"
                                    element={<CMSImportDetailPage />}
                                  />

                                  {/* Bulk FinchGPT Actions */}
                                  <Route
                                    path="/bulk-finchgpt-actions"
                                    element={<BulkAgentOperationsPage />}
                                  />
                                  <Route
                                    path="/bulk-finchgpt-actions/create"
                                    element={<BulkFinchGPTActions />}
                                  />
                                  <Route
                                    path="/bulk-finchgpt-actions/:operationId"
                                    element={<BulkAgentOperationDetailPage />}
                                  />

                                  {/* Leads routes */}
                                  <Route
                                    path="/leads"
                                    element={<LeadsPage />}
                                  />

                                  {/* Intakes routes */}
                                  <Route
                                    path="/intakes"
                                    element={<IntakesPage />}
                                  />
                                  <Route
                                    path="/intakes/create"
                                    element={<CreateIntakePage />}
                                  />
                                  <Route
                                    path="/intakes/new"
                                    element={<IntakeFormPage />}
                                  />
                                  <Route
                                    path="/intakes/:intake_id"
                                    element={<IntakeFormPageWrapper />}
                                  />

                                  {/* Templates V2 (Unified) */}
                                  <Route
                                    path="/templates-v2"
                                    element={<TemplatesV2Page />}
                                  />
                                  <Route
                                    path="/templates-v2/:templateId"
                                    element={<TemplateV2DetailPage />}
                                  />
                                  <Route
                                    path="/templates-v2/:templateId/generate"
                                    element={<TemplateGeneratePage />}
                                  />
                                  <Route
                                    path="/templates-v2-blocks"
                                    element={<TemplateBlocksPlayground />}
                                  />

                                  {/* Other routes */}
                                  <Route path="/call/:id" element={<Call />} />
                                  <Route
                                    path="/template-editor"
                                    element={<TemplateManagerPage />}
                                  />
                                  <Route
                                    path="/operator-template-editor"
                                    element={<OperatorTemplateManagerPage />}
                                  />
                                  <Route
                                    path="/bland-command-center"
                                    element={<BlandCommandCenter />}
                                  />
                                  <Route
                                    path="/bpo-command-center"
                                    element={<BPOCommandCenter />}
                                  />
                                  <Route
                                    path="/flock"
                                    element={<InternalPage />}
                                  />
                                  <Route
                                    path="/zaddy"
                                    element={<ZaddyPage />}
                                  />
                                  <Route
                                    path="/zeyom"
                                    element={<ZeyomPage />}
                                  />

                                  {/* Check-In Settings (admin) */}
                                  <Route
                                    path="/admin/checkin-settings"
                                    element={<CheckinSettingsPage />}
                                  />

                                  {/* Entity Sync Suggestions (experimental) */}
                                  <Route
                                    path="/entity-sync/suggestions"
                                    element={<SyncSuggestionCards />}
                                  />
                                  <Route
                                    path="/entity-sync/suggestions-compact"
                                    element={<SyncSuggestionCardsCompact />}
                                  />

                                  {/* LOA Scan (hidden admin) */}
                                  <Route
                                    path="/admin/loa-scan"
                                    element={<LOAScanPage />}
                                  />

                                  {/* Bulk Billing Code (admin tool) */}
                                  <Route
                                    path="/admin/bulk-billing-codes"
                                    element={<BulkBillingCodePage />}
                                  />
                                  <Route
                                    path="/admin/claims-panopticon"
                                    element={<ClaimsPanopticonPage />}
                                  />

                                  {/* Quant Mode (Crashing Out monitoring) */}
                                  <Route
                                    path="/quant-mode"
                                    element={<QuantModePage />}
                                  />
                                  <Route
                                    path="/quant-mode/config"
                                    element={<AgentflowConfigPage />}
                                  />

                                  {/* Risk Overview */}
                                  <Route
                                    path="/risk-overview"
                                    element={<RiskOverviewPage />}
                                  />

                                  {/* Default route */}
                                  <Route
                                    path="*"
                                    element={<Navigate to="/tasks" />}
                                  />
                                </Route>
                              </Route>
                              </Routes>
                            </NavigationBlockerProvider>
                          </FavoritedMattersProvider>
                          </PushNoteToCmsProvider>
                        </PushChangesProvider>
                      </BulkCreateProvider>
                    </TelephonyCtiProvider>
                  </DocumentTitleProvider>
                </Router>
              </SessionManager>
            </NotificationProvider>
          </MuiThemeSync>
        </Provider>
      </Box>
    </ThemeProvider>
  );
};

export default App;
